import {
  WEEKDAY_CHART_PRESETS,
  WEEKLY_CHART_PRESETS,
  SINGLE_DAY_PRESETS
} from "./widgetConstants";


// Custom-date-filter: for a custom range the granularity depends on length —
// ≤30 days daily, 31–90 days 7-day blocks (from range start), >90 days calendar months.
export const buildChartData = (series = [], preset, customRange) => {
  if (preset === "custom" && customRange && customRange.fromDate != null && customRange.toDate != null) {
    return buildCustomBuckets(series, customRange);
  }

  if (SINGLE_DAY_PRESETS.has(preset)) {
    return buildSingleDayBucket(series, preset);
  }

  if (WEEKDAY_CHART_PRESETS.has(preset) || WEEKLY_CHART_PRESETS.has(preset)) {
    return buildDailyPoints(series);
  }

  return [];
};

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SHORT_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_MS = 86400000;

const formatDateWithDay = date => `${SHORT_MONTHS[date.getMonth()]} ${date.getDate()} (${SHORT_WEEKDAYS[date.getDay()]})`;
const formatShort = date => `${SHORT_MONTHS[date.getMonth()]} ${date.getDate()}`;

const pointValue = point => point.count != null ? point.count : (point.rate || 0);
const pointDate = point => new Date(point.date || point.activityDate);
// Rate points carry raw numerator/denominator so buckets recompute the rate from summed counts
// (averaging daily percentages would be wrong); count points are just summed.
const isRatePoint = point => point.numerator != null && point.denominator != null;

const reduceBucket = points => {
  if (points.length && isRatePoint(points[0])) {
    let num = 0, den = 0;
    points.forEach(p => { num += p.numerator || 0; den += p.denominator || 0; });
    return den > 0 ? Math.round((num / den) * 1000) / 10 : 0;
  }
  return parseFloat(points.reduce((total, p) => total + pointValue(p), 0).toFixed(2));
};

const buildSingleDayBucket = (series, preset) => {
  const date = new Date();
  if (preset === "yesterday") date.setDate(date.getDate() - 1);
  const sum = series.reduce((total, point) => total + pointValue(point), 0);
  return [{ label: formatDateWithDay(date), value: parseFloat(sum.toFixed(2)) }];
};


const buildDailyPoints = series =>
  series.map(point => {
    const date = pointDate(point);
    return { label: formatDateWithDay(date), value: pointValue(point) };
  });

const buildCustomBuckets = (series, { fromDate, toDate }) => {
  const days = Math.round((toDate - fromDate) / DAY_MS) + 1;
  if (days <= 30) return buildDailyPoints(series);
  if (days <= 90) return buildWeekBuckets(series, fromDate);
  return buildMonthBuckets(series);
};

// Fixed 7-day blocks counted from the range start; only the last block can be partial.
const buildWeekBuckets = (series, fromDate) => {
  const start = new Date(fromDate);
  start.setHours(0, 0, 0, 0);
  const startMs = start.getTime();
  const buckets = {};
  series.forEach(point => {
    const idx = Math.floor((pointDate(point).getTime() - startMs) / (7 * DAY_MS));
    if (idx < 0) return;
    (buckets[idx] = buckets[idx] || []).push(point);
  });
  return Object.keys(buckets)
    .map(Number)
    .sort((a, b) => a - b)
    .map(idx => {
      const blockStart = new Date(startMs + idx * 7 * DAY_MS);
      const blockEnd = new Date(startMs + (idx * 7 + 6) * DAY_MS);
      return { label: `${formatShort(blockStart)} – ${formatShort(blockEnd)}`, value: reduceBucket(buckets[idx]) };
    });
};

// Calendar months, e.g. "Jul 2026".
const buildMonthBuckets = series => {
  const buckets = {};
  series.forEach(point => {
    const d = pointDate(point);
    const key = d.getFullYear() * 12 + d.getMonth();
    (buckets[key] = buckets[key] || []).push(point);
  });
  return Object.keys(buckets)
    .map(Number)
    .sort((a, b) => a - b)
    .map(key => {
      const d = pointDate(buckets[key][0]);
      return { label: `${SHORT_MONTHS[d.getMonth()]} ${d.getFullYear()}`, value: reduceBucket(buckets[key]) };
    });
};


const NICE_INTERVALS = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000];

export const getNiceMax = val => {
  if (val <= 10) return { max: 10, tickAmount: 5 };
  const closest = NICE_INTERVALS.reduce((prev, curr) =>
    Math.abs(curr - val / 4) < Math.abs(prev - val / 4) ? curr : prev
  );
  const max = Math.ceil(val / closest) * closest;
  const tickAmount = max / closest;
  return { max, tickAmount };
};

export const resolveChartData = (apiResponse, preset, customRange) => {
  if (apiResponse?.chartData?.length) {
    return apiResponse.chartData;
  }
  return buildChartData(apiResponse?.series || [], preset, customRange);
};
